/**
 * Demo Loading State — Module-level store
 *
 * REQUIREMENTS SUMMARY:
 * - Stores isEnteringDemo / isExitingDemo outside of React component state.
 * - Survives React component unmount/remount (e.g. when SubscriptionGuard
 *   re-renders during org switch, causing DemoProvider to remount).
 * - Compatible with React 18 useSyncExternalStore for safe concurrent reads.
 * - DemoContext writes to this store; DemoLoadingOverlay reads from it.
 * - The overlay can be placed above DemoProvider in the component tree.
 */

type Listener = () => void;

interface DemoLoadingSnapshot {
  isEntering: boolean;
  isExiting: boolean;
}

let _isEntering = false;
let _isExiting = false;
let _snapshot: DemoLoadingSnapshot = { isEntering: false, isExiting: false };
const listeners = new Set<Listener>();

function updateSnapshot() {
  _snapshot = { isEntering: _isEntering, isExiting: _isExiting };
}

function notify() {
  listeners.forEach((fn) => fn());
}

export const demoLoadingState = {
  /** Returns a cached object — same reference until values change. */
  getSnapshot(): DemoLoadingSnapshot {
    return _snapshot;
  },

  setEntering(val: boolean) {
    if (_isEntering !== val) {
      _isEntering = val;
      updateSnapshot();
      notify();
    }
  },

  setExiting(val: boolean) {
    if (_isExiting !== val) {
      _isExiting = val;
      updateSnapshot();
      notify();
    }
  },

  subscribe(fn: Listener): () => void {
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  },
};
